import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import { AlertCircle, Check, Globe, Loader2, Plus } from 'lucide-react';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import Button from '../../../components/Button/Button';
import {
  isCrawledItem,
  KnowledgeBaseItemResponse
} from '../../../types/KnowledgeBaseItemResponse';
import KnowledgeBaseService from '../../../services/KnowledgeBaseService/KnowledgeBaseService';
import useAuthRoles from '../../../hooks/useAuthRoles';
import DocumentPreview from '../../../components/DocumentPreview/DocumentPreview';
import useAbortController from '../../../hooks/useAbortController';
import NotificationSnackbar from '../../../components/NotificationSnackbar/NotificationSnackbar';
import { HeaderDefinition } from '../../../components/DataTable/DataTableHeader';
import { TableCell } from '../../../components/ui/table';
import KnowledgeBaseActionsDropdown from '../../../components/KnowledgeBaseActionsDropdown/KnowledgeBaseActionsDropdown';
import { DocumentResponse } from '../../../types/DocumentResponse';
import DataTable from '../../../components/DataTable/DataTable';
import { Switch } from '../../../components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../components/ui/tooltip';
import UploadKnowledgeBaseItemModal from '../../../components/UploadKnowledgeBaseItemModal/UploadKnowledgeBaseItemModal';
import EditKnowledgeBaseItemModal from '../../../components/EditKnowledgeBaseItemModal/EditKnowledgeBaseItemModal';
import SearchBar from '../../../components/SearchBar/SearchBar';
import HighlightText from '../../../components/HighlightText/HighlightText';
import WebsiteSourceDialog from '../WebsitesTab/WebsiteSourceDialog';

/** Status cell: tick when embedded, spinner while queued, warning with the reason when failed. */
export function IngestionStatusCell({
  item
}: Readonly<{ item: KnowledgeBaseItemResponse }>): React.ReactElement {
  if (item.ingestionStatus === 'INGESTED') {
    return (
      <Check size={16} className='text-green-600' aria-label='Processed' />
    );
  }
  if (item.ingestionStatus === 'FAILED') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className='inline-flex items-center gap-1 text-destructive'
            aria-label='Processing failed'
          >
            <AlertCircle size={16} />
            <span className='text-xs'>Failed</span>
          </span>
        </TooltipTrigger>
        <TooltipContent className='max-w-sm break-words'>
          {item.ingestionError ?? 'Processing failed'}
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Loader2
      size={16}
      className='animate-spin text-muted-foreground'
      aria-label='Processing'
    />
  );
}

function KnowledgeBaseTab(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState<string>(searchParams.get('q') || '');
  const [page, setPage] = useState<number>(+searchParams.get('page')! || 0);
  const [numberOfResults, setNumberOfResults] = useState<number>(0);
  const [searchData, setSearchData] = useState<KnowledgeBaseItemResponse[]>();
  const [searchIsLoading, setSearchIsLoading] = useState<boolean>(true);
  const [resultsPerPage, setResultsPerPage] = useState<number>(
    +searchParams.get('resultsPerPage')! || 25
  );
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  const [isWebsiteDialogOpen, setIsWebsiteDialogOpen] =
    useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedItemForEdit, setSelectedItemForEdit] =
    useState<KnowledgeBaseItemResponse | null>(null);
  const [sortColumn, setSortColumn] = useState<string>(
    searchParams.get('sortColumn') || 'name'
  );
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>(
    (searchParams.get('sortDirection') as 'ASC' | 'DESC') || 'ASC'
  );
  const [preSelectedFile, setPreSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  const location = useLocation();
  const { currentChatbot } = React.useContext(ChatbotContext);
  const { userCanWrite } = useAuthRoles();
  const { getAbortSignal } = useAbortController();

  const handleFetchKnowledgeBase = useCallback((): void => {
    if (!currentChatbot) return;
    const signal = getAbortSignal();
    setSearchIsLoading(true);

    KnowledgeBaseService.getKnowledgeBase(
      currentChatbot.id,
      {
        page,
        resultsPerPage,
        sortColumn,
        sortDirection,
        query: query || undefined
      },
      signal
    )
      .then((data) => {
        setSearchParams((params) => {
          params.set('search', query ?? '');
          params.set('page', page.toString());
          params.set('resultsPerPage', resultsPerPage.toString());
          params.set('sortColumn', sortColumn);
          params.set('sortDirection', sortDirection);
          return params;
        });
        setNumberOfResults(data.numberOfResults);
        setSearchData(data.results);
        setHasError(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setNumberOfResults(0);
        setSearchData([]);
        setHasError(true);
      })
      .finally(() => {
        setSearchIsLoading(false);
      });
  }, [
    currentChatbot,
    query,
    page,
    resultsPerPage,
    sortColumn,
    sortDirection,
    getAbortSignal,
    setSearchParams
  ]);

  useEffect(() => {
    handleFetchKnowledgeBase();
  }, [handleFetchKnowledgeBase]);

  useEffect(() => {
    setPage(0);
  }, [query]);

  // Poll only while something is still queued; failed items do not keep the page refreshing.
  useEffect(() => {
    const hasPendingItems = searchData?.some(
      (item) => item.ingestionStatus === 'PENDING'
    );
    if (!hasPendingItems) return undefined;

    const interval = setInterval(() => {
      handleFetchKnowledgeBase();
    }, 5000);

    return (): void => clearInterval(interval);
  }, [searchData, handleFetchKnowledgeBase]);

  const handleRowClick = async (
    kbItem: KnowledgeBaseItemResponse | DocumentResponse
  ): Promise<void> => {
    if (currentChatbot) {
      try {
        const { filePreviewUrl: previewUrl } =
          await KnowledgeBaseService.viewKnowledgeBaseItem(
            currentChatbot.id,
            kbItem.id
          );
        window.open(previewUrl, '_blank');
      } catch {
        setErrorMessage(
          'Unable to view knowledge base item. Please try again.'
        );
        setShowError(true);
      }
    }
  };

  const handleEditItem = (item: KnowledgeBaseItemResponse): void => {
    setSelectedItemForEdit(item);
    setIsEditModalOpen(true);
  };

  const handleToggleEnabled = async (
    item: KnowledgeBaseItemResponse,
    enabled: boolean
  ): Promise<void> => {
    if (!currentChatbot) return;

    setSearchData((prev) =>
      prev?.map((kb) => (kb.id === item.id ? { ...kb, enabled } : kb))
    );

    try {
      await KnowledgeBaseService.setKnowledgeBaseItemEnabled(
        currentChatbot.id,
        item,
        enabled
      );
    } catch {
      setSearchData((prev) =>
        prev?.map((kb) =>
          kb.id === item.id ? { ...kb, enabled: !enabled } : kb
        )
      );
      setErrorMessage(
        'Unable to update knowledge base item. Please try again.'
      );
      setShowError(true);
    }
  };

  const deleteKnowledgeBaseItem = (
    document: KnowledgeBaseItemResponse
  ): Promise<void> => {
    return KnowledgeBaseService.deleteKnowledgeBaseItem(
      currentChatbot!.id,
      document.id
    )
      .then(handleFetchKnowledgeBase)
      .catch((err) => {
        setErrorMessage(
          err?.response?.data?.message ||
            'We were unable to delete your knowledge base item. Please try again.'
        );
        setShowError(true);
      });
  };

  const retryIngestion = (item: KnowledgeBaseItemResponse): Promise<void> => {
    return KnowledgeBaseService.retryIngestion(currentChatbot!.id, item.id)
      .then(handleFetchKnowledgeBase)
      .catch(() => {
        setErrorMessage('Unable to retry processing. Please try again.');
        setShowError(true);
      });
  };

  const validateFile = (file: File): boolean => {
    const validTypes = ['text/plain', 'text/markdown', 'application/pdf'];
    const validExtensions = ['.txt', '.md', '.pdf'];

    const hasValidType = validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    return hasValidType || hasValidExtension;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragleave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const { files } = e.dataTransfer;

    if (files.length !== 1) return;

    const file = files[0];

    if (!validateFile(file)) return;

    if (isUploadModalOpen || isEditModalOpen || isWebsiteDialogOpen) return;

    setPreSelectedFile(file);
    setIsUploadModalOpen(true);
  };

  const knowledgeBaseHeaders: HeaderDefinition[] = [
    {
      key: 'name',
      label: 'Item name',
      sortable: true,
      className: 'w-[18%]'
    },
    {
      key: 'description',
      label: 'Description',
      className: 'w-[22%]'
    },
    {
      key: 'sourceUrl',
      label: 'Source',
      sortable: true,
      className: 'w-[16%]'
    },
    {
      key: 'nextReviewDate',
      label: 'Next review date',
      sortable: true,
      className: 'w-[14%]'
    },
    {
      key: 'status',
      label: 'Status',
      className: 'w-[8%]'
    },
    {
      key: 'enabled',
      label: 'Enabled',
      className: 'w-[10%]'
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'w-[12%]'
    }
  ];

  const renderSource = (item: KnowledgeBaseItemResponse): React.ReactNode => {
    if (!isCrawledItem(item) || !item.sourceUrl) {
      return <span className='text-xs text-muted-foreground'>Upload</span>;
    }
    return (
      <div className='min-w-0'>
        <a
          href={item.sourceUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='block truncate text-xs underline-offset-2 hover:underline'
          title={item.sourceUrl}
          onClick={(e) => e.stopPropagation()}
        >
          <Globe size={12} className='mr-1 inline' />
          {item.sourceUrl}
        </a>
        {item.goneSince && (
          <span className='text-xs text-amber-700'>
            Gone since {new Date(item.goneSince).toLocaleDateString()}
            {item.goneReason
              ? ` (${item.goneReason.toLowerCase().replace('_', ' ')})`
              : ''}
          </span>
        )}
      </div>
    );
  };

  const renderRow = (item: KnowledgeBaseItemResponse): React.ReactElement => {
    return (
      <>
        <TableCell className='w-[18%]'>
          <span className='block truncate pr-2' title={item.name}>
            <HighlightText text={item.name} searchQuery={query} size='sm' />
          </span>
        </TableCell>
        <TableCell className='w-[22%]'>
          <div className='max-w-full overflow-hidden'>
            <div className='text-sm line-clamp-2 pr-2' title={item.description}>
              <HighlightText
                text={item.description || ''}
                searchQuery={query}
                size='sm'
              />
            </div>
          </div>
        </TableCell>
        <TableCell className='w-[16%]'>{renderSource(item)}</TableCell>
        <TableCell className='w-[14%]'>{item.nextReviewDate}</TableCell>
        <TableCell className='w-[8%]'>
          <IngestionStatusCell item={item} />
        </TableCell>
        <TableCell className='w-[10%]'>
          <Switch
            checked={item.enabled}
            disabled={item.ingestionStatus !== 'INGESTED'}
            onCheckedChange={(checked) => handleToggleEnabled(item, checked)}
            aria-label={`${item.enabled ? 'Disable' : 'Enable'} ${item.name}`}
          />
        </TableCell>
        <TableCell className='w-[12%]'>
          <KnowledgeBaseActionsDropdown
            item={item}
            onView={handleRowClick}
            onEdit={handleEditItem}
            onDelete={deleteKnowledgeBaseItem}
            onRetry={retryIngestion}
          />
        </TableCell>
      </>
    );
  };

  if (!userCanWrite()) {
    return <Navigate to='/chats' state={{ from: location }} replace />;
  }

  return (
    <TooltipProvider>
      <div className='flex flex-1 flex-col'>
        <Helmet>
          <title>Japeto - Knowledge Base</title>
        </Helmet>
        <div className='flex items-center justify-between gap-4 mb-4'>
          <SearchBar
            setQuery={setQuery}
            searchTermFromState={query}
            placeholder='Search knowledgebase...'
          />
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={() => setIsWebsiteDialogOpen(true)}
              icon={<Globe size='16' />}
            >
              Add website
            </Button>
            <Button
              onClick={() => setIsUploadModalOpen(true)}
              icon={<Plus size='16' />}
            >
              Add document
            </Button>
          </div>
        </div>

        <NotificationSnackbar
          message={errorMessage}
          severity='error'
          open={showError}
          onClose={() => setShowError(false)}
        />
        <DocumentPreview handleRowClick={handleRowClick}>
          <section
            onDragOver={handleDragOver}
            onDragLeave={handleDragleave}
            onDrop={handleDrop}
            className='flex-1'
            aria-label='Knowledge base table with drag and drop support'
          >
            <DataTable<KnowledgeBaseItemResponse>
              searchData={searchData || null}
              numberOfResults={numberOfResults}
              resultsPerPage={resultsPerPage}
              isLoading={searchIsLoading}
              setResultsPerPage={setResultsPerPage}
              setPage={setPage}
              page={page}
              tableHeaders={knowledgeBaseHeaders}
              renderRow={renderRow}
              sortColumn={sortColumn}
              setSortColumn={setSortColumn}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              onSortChange={handleFetchKnowledgeBase}
              hasError={hasError}
              handleFetch={handleFetchKnowledgeBase}
              noDataTitle='No documents added'
              noDataMessage='Upload a document or add a website to train your chatbot on a custom knowledgebase'
              hasActiveSearch={!!query}
              noSearchResultsTitle='No documents found'
              noSearchResultsMessage="There aren't any documents matching your search."
              isDragOver={isDragOver}
              isDropZone
            />
          </section>
        </DocumentPreview>

        <UploadKnowledgeBaseItemModal
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setPreSelectedFile(null);
          }}
          onSuccess={handleFetchKnowledgeBase}
          preSelectedFile={preSelectedFile ?? undefined}
        />

        <WebsiteSourceDialog
          isOpen={isWebsiteDialogOpen}
          source={null}
          onClose={() => setIsWebsiteDialogOpen(false)}
          onSaved={handleFetchKnowledgeBase}
        />

        <EditKnowledgeBaseItemModal
          isOpen={isEditModalOpen}
          item={selectedItemForEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItemForEdit(null);
          }}
          onSuccess={handleFetchKnowledgeBase}
        />
      </div>
    </TooltipProvider>
  );
}

export default KnowledgeBaseTab;
